import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Trophy, ThumbsUp } from "lucide-react";

export default function Challenge() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ title: "", pitch: "" });
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => api.get("/challenge/current").then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!user) { toast.error("Sign in to submit"); return nav("/auth"); }
    if (!form.title.trim() || !form.pitch.trim()) return toast.error("Fill both fields");
    await api.post("/challenge/submit", form);
    setForm({ title: "", pitch: "" });
    toast.success("Entry submitted!");
    load();
  };

  const vote = async (id) => { await api.post(`/challenge/${id}/vote`); load(); };

  if (!data) return <Container><div className="font-heading">Loading...</div></Container>;

  return (
    <Container>
      <NbCard color="tertiary" className="!p-8 mb-8" data-testid="challenge-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Chip color="primary">MONTH · {data.month}</Chip>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mt-3">Monthly Idea Challenge</h1>
            <p className="mt-3 text-lg italic max-w-xl">"{data.prompt}"</p>
          </div>
          <Trophy className="w-16 h-16" />
        </div>
      </NbCard>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <NbCard className="md:col-span-2 !p-6" data-testid="challenge-form">
          <h3 className="font-heading font-black text-2xl tracking-tight">Enter this month</h3>
          <input data-testid="chal-title" placeholder="Entry title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-3 w-full px-4 py-3 nb-border rounded-lg" />
          <textarea data-testid="chal-pitch" placeholder="Your 60-second pitch..." rows={5} value={form.pitch} onChange={(e) => setForm({ ...form, pitch: e.target.value })} className="mt-3 w-full px-4 py-3 nb-border rounded-lg" />
          <div className="mt-3"><NbButton onClick={submit} color="black" data-testid="submit-chal">Submit entry</NbButton></div>
        </NbCard>

        <div className="md:col-span-3">
          <h3 className="font-heading font-black text-2xl tracking-tight mb-3">Leaderboard</h3>
          <div className="space-y-3" data-testid="leaderboard">
            {data.entries.length === 0 && <div className="text-neutral-500">Be the first to enter this month.</div>}
            {data.entries.map((e, i) => (
              <NbCard key={e.id} className="!p-4 flex items-center gap-4">
                <div className="font-heading font-black text-3xl w-10 text-center">{i + 1}</div>
                <div className="flex-1">
                  <div className="font-heading font-bold">{e.title}</div>
                  <div className="text-sm text-neutral-700">{e.pitch}</div>
                  <div className="font-mono-sl text-[10px] uppercase text-neutral-500 mt-1">by {e.author}</div>
                </div>
                <button onClick={() => vote(e.id)} data-testid={`vote-${e.id}`} className="px-3 py-2 nb-border rounded-lg bg-sl-primary nb-hover flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> {e.votes}</button>
              </NbCard>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
}
